import React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { Course, Degree } from "../../../graphql/generated/graphql";

interface DegreeDetailProps {
  degreeId: string;
  onBackToDegrees: () => void;
  onCourseSelect: (courseId: string) => void;
  degreesData: Degree[];
  coursesByDegreeData: Course[];
  loading: boolean;
}

const DegreeDetail: React.FC<DegreeDetailProps> = ({
  degreeId,
  onBackToDegrees,
  onCourseSelect,
  degreesData,
  coursesByDegreeData,
  loading,
}) => {
  const degree = degreesData?.find((d) => d.id === degreeId);

  if (loading) {
    return (
      <View style={styles.container}>
        <CustomText text="Loading degree details..." size="m" useTranslationText={false} />
      </View>
    );
  }

  if (!degree) {
    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.backButton} onPress={onBackToDegrees}>
          <CustomText text="← Back to Degrees" size="m" useTranslationText={false} />
        </TouchableOpacity>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="Degree not found" size="m" useTranslationText={false} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.backButton} onPress={onBackToDegrees}>
        <CustomText text="← Back to Degrees" size="m" useTranslationText={false} />
      </TouchableOpacity>

      <CustomSpacing type="vertical" size="m" />

      <CustomText text={degree.name} size="xl" weight="bold" useTranslationText={false} />

      {degree.description && (
        <CustomText text={degree.description} size="m" useTranslationText={false} style={styles.description} />
      )}

      <CustomSpacing type="vertical" size="l" />

      <CustomText text="Courses in this Degree" size="l" weight="bold" useTranslationText={false} />

      <CustomSpacing type="vertical" size="m" />

      {coursesByDegreeData?.length === 0 ? (
        <CustomText text="No courses found for this degree" size="m" useTranslationText={false} />
      ) : (
        <View style={styles.coursesList}>
          {coursesByDegreeData?.map((course) => (
            <TouchableOpacity key={course.id} style={styles.courseCard} onPress={() => onCourseSelect(course.id)}>
              <CustomText text={course.name || "Unnamed Course"} weight="bold" size="m" useTranslationText={false} />
              <CustomText text={`Course Code: ${course.course_code || "N/A"}`} size="s" useTranslationText={false} />
              <CustomText text={`Length: ${course.lengthInWeeks || "N/A"} weeks`} size="s" useTranslationText={false} />
              <CustomText
                text={`Enrolled Students: ${course.enrolledStudents?.length || 0}`}
                size="s"
                useTranslationText={false}
              />
              <CustomSpacing type="vertical" size="s" />
              <CustomText
                text="Tap to view analytics →"
                size="xs"
                style={styles.actionText}
                useTranslationText={false}
              />
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  backButton: {
    alignSelf: "flex-start",
    padding: 8,
  },
  description: {
    color: "#666",
    marginTop: 8,
  },
  coursesList: {
    gap: 12,
  },
  courseCard: {
    padding: 16,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
  },
  actionText: {
    color: "#0084FF",
    fontStyle: "italic",
  },
});

export default DegreeDetail;
